import {WriteButton} from '../../common/buttons/WriteActions';
import {useDelayedNameError, nameErrorHelperSx} from '../../../hooks/useDelayedNameError';
import {validateCatalogName} from '../../../common/catalogNameValidation';
import {mobileDialogSx} from '../../common/dialogs/mobileDialogSx';
import {useEffect, useState} from 'react';
import {Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Stack, TextField} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import {ItemDto} from '../../../models/Items/IItem';
import {itemAPI} from '../../../services/ItemServices';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {getApiErrorKind, getApiErrorMessage, getApiFieldErrors, getApiTraceId} from '../../../common/apiErrors';
import TraceIdFeedback from '../../feedback/TraceIdFeedback';
import {DeleteItemDialog} from './DeleteItemDialog';

type Props = { item: ItemDto | undefined; open: boolean; onClose: () => void; };

export const EditIngredientDialog = ({item, open, onClose}: Props) => {
    const {t} = useAppTranslation();
    const {data: existingNames, isFetching: namesFetching, error: namesError} = itemAPI.useGetAvailableItemNamesQuery(undefined, {skip: !open, refetchOnMountOrArgChange: true});
    const [name, setName] = useState(item?.name ?? '');
    const [updateItem, {isLoading, error}] = itemAPI.useUpdateItemMutation();
    const [deleteItemRequest, {isLoading: isDeleting}] = itemAPI.useDeleteItemMutation();
    const [serverToast, setServerToast] = useState('');
    const [traceId, setTraceId] = useState('');
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    useEffect(() => setName(item?.name ?? ''), [item]);

    const nameValidation = validateCatalogName(name, existingNames, item?.name ?? undefined);
    const nameError = useDelayedNameError(name, nameValidation === 'too_short' ? t('common:name_min_length') : nameValidation === 'duplicate' ? t('items:ingredient_name_exists') : '');
    const nameInvalid = !!nameValidation || !existingNames || namesFetching || !!namesError;
    const submit = async () => {
        if (!item || nameInvalid || isLoading || isDeleting) return;
        try {
            await updateItem({id: item.id, body: {name: name.trim(), unitOfWeight: item.unitOfWeight}}).unwrap();
            onClose();
        } catch (requestError) {
            if (getApiErrorKind(requestError) === 'server') {
                setServerToast(getApiErrorMessage(requestError, t));
                setTraceId(getApiTraceId(requestError));
            }
        }
    };

    const openDelete = () => {
        setDeleteError('');
        setDeleteOpen(true);
    };

    const deleteItem = async () => {
        if (!item) return false;
        setDeleteError('');
        try {
            await deleteItemRequest(item.id).unwrap();
            onClose();
            return true;
        } catch (requestError) {
            setDeleteError(getApiErrorMessage(requestError, t));
            return false;
        }
    };

    return (
        <Dialog sx={mobileDialogSx} open={open} onClose={isLoading || isDeleting ? undefined : onClose} fullWidth maxWidth="xs" PaperProps={{sx: {borderRadius: 3}}}>
            <DialogTitle>{t('items:edit_ingredient')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{pt: 1}}>{namesError && <Alert severity="error">{getApiErrorMessage(namesError, t)}</Alert>}
                    {error && getApiErrorKind(error) !== 'server' && !getApiFieldErrors(error, t).name && <Alert severity="error">{getApiErrorMessage(error, t)}</Alert>}
                    <TextField autoFocus required fullWidth label={t('items:item_name')} value={name}
                               onChange={(event) => setName(event.target.value)} error={!!nameError || !!getApiFieldErrors(error, t).name} helperText={nameError || getApiFieldErrors(error, t).name || ' '} FormHelperTextProps={{sx: nameErrorHelperSx}}/>
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <WriteButton color="error" onClick={openDelete} disabled={!item || isLoading || isDeleting} startIcon={<DeleteIcon/>}>{t('common:delete')}</WriteButton>
                <Button onClick={onClose} disabled={isLoading}>{t('common:cancel')}</Button>
                <WriteButton variant="contained" onClick={submit} disabled={nameInvalid || isLoading || isDeleting} startIcon={<SaveIcon/>}>{t('common:save')}</WriteButton>
            </DialogActions>
            {item && <DeleteItemDialog open={deleteOpen} onClose={() => setDeleteOpen(false)} itemName={item.name ?? ''} onDelete={deleteItem} error={deleteError}/>}
            <Snackbar open={!!serverToast} autoHideDuration={6000} onClose={() => setServerToast('')}>
                <Alert severity="error" onClose={() => setServerToast('')}>{serverToast}<TraceIdFeedback traceId={traceId}/></Alert>
            </Snackbar>
        </Dialog>
    );
};
